import React, { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "reactstrap";

import { buildWeekDayDates, formatIsoDate } from "../../../../helpers/agendaCalendarStorage";

const SWIPE_THRESHOLD_PX = 48;

type AgendaWeekDayStripProps = {
  selectedDate: string;
  weekMode: boolean;
  onSelectDate: (isoDate: string) => void;
  onSelectWeek: () => void;
  onNavigateWeek?: (direction: "prev" | "next") => void;
  onToday?: () => void;
};

const AgendaWeekDayStrip = ({
  selectedDate,
  weekMode,
  onSelectDate,
  onSelectWeek,
  onNavigateWeek,
  onToday,
}: AgendaWeekDayStripProps) => {
  const { t, i18n } = useTranslation();
  const touchStartXRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);

  const weekDays = useMemo(
    () => buildWeekDayDates(new Date(`${selectedDate}T12:00:00`)),
    [selectedDate]
  );

  const todayIso = formatIsoDate(new Date());
  const showTodayButton = Boolean(onToday) && (weekMode || selectedDate !== todayIso);

  const weekdayFormatter = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { weekday: "short" }),
    [i18n.language]
  );

  const dayFormatter = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { day: "2-digit", month: "2-digit" }),
    [i18n.language]
  );

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    touchStartXRef.current = event.touches[0]?.clientX ?? null;
    touchStartYRef.current = event.touches[0]?.clientY ?? null;
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (!onNavigateWeek || touchStartXRef.current === null || touchStartYRef.current === null) {
      touchStartXRef.current = null;
      touchStartYRef.current = null;
      return;
    }

    const endX = event.changedTouches[0]?.clientX ?? touchStartXRef.current;
    const endY = event.changedTouches[0]?.clientY ?? touchStartYRef.current;
    const deltaX = endX - touchStartXRef.current;
    const deltaY = endY - touchStartYRef.current;

    touchStartXRef.current = null;
    touchStartYRef.current = null;

    if (Math.abs(deltaX) < SWIPE_THRESHOLD_PX || Math.abs(deltaX) < Math.abs(deltaY)) {
      return;
    }

    onNavigateWeek(deltaX > 0 ? "prev" : "next");
  };

  return (
    <div className="agenda-week-day-strip-wrapper mb-0">
      <div
        className="agenda-week-day-strip d-flex align-items-stretch gap-2"
        role="toolbar"
        aria-label={t("studio.agenda.calendar.weekStripLabel")}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {onNavigateWeek ? (
          <div
            className="btn-group agenda-week-day-strip__nav"
            role="group"
            aria-label={t("studio.agenda.calendar.navGroupLabel")}
          >
            <Button
              color="secondary"
              outline
              size="sm"
              className="agenda-week-nav-btn bg-white text-body border-secondary-subtle"
              onClick={() => onNavigateWeek("prev")}
              aria-label={t("studio.agenda.calendar.navPrevious")}
            >
              ←
            </Button>
            <Button
              color="secondary"
              outline
              size="sm"
              className="agenda-week-nav-btn bg-white text-body border-secondary-subtle"
              onClick={() => onNavigateWeek("next")}
              aria-label={t("studio.agenda.calendar.navNext")}
            >
              →
            </Button>
          </div>
        ) : null}
        {showTodayButton && onToday ? (
          <Button
            color="secondary"
            outline
            size="sm"
            className="agenda-week-today-btn bg-white text-body border-secondary-subtle"
            onClick={onToday}
          >
            {t("studio.agenda.calendar.today")}
          </Button>
        ) : null}
        <Button
          color={weekMode ? "success" : "secondary"}
          outline={!weekMode}
          size="sm"
          className={`agenda-week-mode-btn ${weekMode ? "" : "bg-white text-body border-secondary-subtle"}`.trim()}
          onClick={onSelectWeek}
          aria-pressed={weekMode}
        >
          {t("studio.agenda.calendar.viewWeek")}
        </Button>
        {weekDays.map((date) => {
          const isoDate = formatIsoDate(date);
          const isSelected = !weekMode && isoDate === selectedDate;
          const isToday = isoDate === todayIso;

          return (
            <Button
              key={isoDate}
              color={isSelected ? "success" : "secondary"}
              outline={!isSelected}
              size="sm"
              className={`agenda-week-day-btn ${isSelected ? "" : "bg-white text-body border-secondary-subtle"} ${isToday ? "agenda-week-day-btn--today" : ""}`.trim()}
              onClick={() => onSelectDate(isoDate)}
              aria-pressed={isSelected}
              aria-current={isSelected ? "date" : undefined}
            >
              <span className="agenda-week-day-btn__weekday">{weekdayFormatter.format(date)}</span>
              <span className="agenda-week-day-btn__date">{dayFormatter.format(date)}</span>
              {isToday ? (
                <span className="agenda-week-day-btn__today-badge">
                  {t("studio.agenda.calendar.todayBadge")}
                </span>
              ) : null}
            </Button>
          );
        })}
      </div>
      {onNavigateWeek ? (
        <p className="agenda-week-day-strip__swipe-hint text-muted small mb-0 mt-2 d-md-none">
          {t("studio.agenda.calendar.weekStripSwipeHint")}
        </p>
      ) : null}
    </div>
  );
};

export default AgendaWeekDayStrip;
